import { FilterDocument, ID, IDPrefixes, PrefixedID } from '@griply/types';
import {
  defaultTaskListGroupSettings,
  getPrefixedId,
  getPrefixedIdPrefix,
  isDefaultFilterDocument,
  normalizeForSearch,
  normalizeSortingMode,
} from '@griply/utils';
import { TreeInstance } from '@headless-tree/core';
import { useSortedGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useSortedTags } from 'features/tags';
import { useTasksByFilterDocument } from 'features/tasks';
import React, { useMemo } from 'react';
import { TaskCardContextMenu } from 'shared/components/connected/task-card-context-menu';
import {
  TaskCardMultiSelectContextMenu,
  useTaskTreeShortcuts,
} from 'shared/components/connected/task-card-multi-select-context-menu';
import {
  InternalTreeItem,
  useTreeValue,
} from 'shared/components/ui/virtualized-tree-list';
import { useRegisterFullCalendarDraggableItems } from 'shared/hooks/use-register-full-calendar-draggable-items';
import {
  useRelationships,
  useRelationshipsMap,
} from 'shared/hooks/use-relationships';
import { useUser } from 'shared/hooks/use-user';
import { GroupByMode } from 'shared/types/sorting-mode';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import { NoFilterResultsState } from './components/no-filter-results-state';
import { NoSearchResultsState } from './components/no-search-results-state';
import { ContextMenuProvider, useContextMenu } from './context-menu-context';
import { groupByLists } from './groups';
import { TaskFilterListProps } from './types';

const TaskFilterListContent: React.FC<TaskFilterListProps> = ({
  filterDocument,
  onReorder,
  customOrder,
  onSectionCollapse,
  scrollElement,
  includeDuration,
  onReset,
  denseFormat,
  includeTaskWithAction,
  groupSettings,
  groupSettingOverrides,
  defaultFilterDocument,
  excludeHiddenTodoHabits,
  defaultNewTaskDate,
  includeArchivedGoals,
  includeArchived,
  goals: goalsProp,
  canReorderGoals,
  EmptyState,
  searchQuery,
}) => {
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const sortedGoals = useSortedGoals();
  const goals = goalsProp ?? sortedGoals;
  const tags = useSortedTags();

  // get tasks by filterDocument
  const allTasks = useTasksByFilterDocument(filterDocument, {
    includeTaskWithAction,
    excludeHiddenTodoHabits,
    includeArchivedGoals,
    includeArchived,
  });

  const relationships = useRelationships();
  const relationshipsMap = useRelationshipsMap(relationships);

  const normalizedSearchQuery = normalizeForSearch(searchQuery?.trim() ?? '');
  const hasSearchQuery = !!normalizedSearchQuery;

  const taskSearchIndex = useMemo(() => {
    const index = new Map<
      ID,
      { nameNormalized: string; descriptionNormalized: string }
    >();
    if (!hasSearchQuery) {
      return index;
    }
    allTasks.forEach((task) => {
      index.set(task.id, {
        nameNormalized: normalizeForSearch(task.name ?? ''),
        descriptionNormalized: normalizeForSearch(task.description ?? ''),
      });
    });
    return index;
  }, [allTasks, hasSearchQuery]);

  const taskTagIdsMap = useMemo(() => {
    const map = new Map<ID, Set<ID>>();
    if (!hasSearchQuery) {
      return map;
    }
    Object.entries(relationshipsMap).forEach(([prefixedId, refs]) => {
      if (getPrefixedIdPrefix(prefixedId as PrefixedID) !== IDPrefixes.Task) {
        return;
      }
      const tagIds = new Set<ID>();
      refs.forEach((ref) => {
        if (getPrefixedIdPrefix(ref) === IDPrefixes.Tag) {
          tagIds.add(getPrefixedId(ref));
        }
      });
      if (!tagIds.size) {
        return;
      }
      map.set(getPrefixedId(prefixedId as PrefixedID), tagIds);
    });
    return map;
  }, [hasSearchQuery, relationshipsMap]);

  const tasks = useMemo(() => {
    if (!normalizedSearchQuery) {
      return allTasks;
    }
    const matchingTagIds = new Set(
      tags
        .filter((tag) =>
          normalizeForSearch(tag.name).includes(normalizedSearchQuery),
        )
        .map((tag) => tag.id),
    );
    const matchingGoalIds = new Set(
      goals
        .filter((goal) =>
          normalizeForSearch(goal.name ?? '').includes(normalizedSearchQuery),
        )
        .map((goal) => goal.id),
    );
    return allTasks.filter((task) => {
      const indexEntry = taskSearchIndex.get(task.id);
      if (indexEntry?.nameNormalized.includes(normalizedSearchQuery)) {
        return true;
      }
      if (indexEntry?.descriptionNormalized.includes(normalizedSearchQuery)) {
        return true;
      }
      if (task.goalId && matchingGoalIds.has(task.goalId)) {
        return true;
      }
      if (!matchingTagIds.size) {
        return false;
      }
      const taskTagIds = taskTagIdsMap.get(task.id);
      if (!taskTagIds) {
        return false;
      }
      for (const tagId of taskTagIds) {
        if (matchingTagIds.has(tagId)) {
          return true;
        }
      }
      return false;
    });
  }, [
    allTasks,
    goals,
    tags,
    taskSearchIndex,
    taskTagIdsMap,
    normalizedSearchQuery,
  ]);

  const collapsedSections = useMemo(
    () => filterDocument.collapsedSections ?? [],
    [filterDocument.collapsedSections],
  );

  useRegisterFullCalendarDraggableItems({
    listRef: scrollElement,
    tasks,
    weekStartsOn,
    includeDuration,
    includeDueDateAsOriginDate: true,
  });

  /*
   * If there are no tasks, we typically force "No Grouping" mode to show the "New Task" row.
   * However, if we are in "Section" mode and have sections, we should stay in "Section" mode
   * to show those empty sections.
   * Same applies for "Goal" mode.
   */
  const sections = groupSettings?.[GroupByMode.Section]?.sections ?? [];

  const hasRequestedStructure =
    (filterDocument.groupByMode === GroupByMode.Section && sections.length) ||
    (filterDocument.groupByMode === GroupByMode.Goal && goals.length);

  const shouldForceNoGrouping =
    !tasks.length &&
    !hasRequestedStructure &&
    filterDocument.groupByMode !== GroupByMode.Section;

  const groupMode = shouldForceNoGrouping
    ? GroupByMode.None
    : filterDocument.groupByMode ?? GroupByMode.None;

  const GroupedList = groupByLists[groupMode] ?? groupByLists[GroupByMode.None];
  const groupSetting = useMemo(
    () => ({
      ...(groupSettings?.[groupMode] ??
        defaultTaskListGroupSettings[groupMode] ??
        groupSettings?.[GroupByMode.None] ??
        defaultTaskListGroupSettings[GroupByMode.None]),
      ...groupSettingOverrides,
      ...(normalizedSearchQuery && { hideNewTaskRow: true }),
    }),
    [groupMode, groupSettingOverrides, groupSettings, normalizedSearchQuery],
  );

  const tree = useTreeValue();
  const { open, position, payload, close } = useContextMenu();

  useTaskTreeShortcuts({
    tree: tree as TreeInstance<InternalTreeItem>,
    tasks,
    open,
    isContextMenuOpen: !!position,
  });

  /*
   * We only want to show the empty state if the user has active filters.
   * If there are no tasks and no filters, we show the list (which will
   * render the "New Task" row).
   *
   * Additionally, we check for `onReset`. If a parent component does NOT
   * provide `onReset` (like GoalDetail), it implies the filters are
   * contextual (base filters) and not user-removable. In that case, we
   * should NOT show the "No results" empty state, but rather the standard
   * empty list (with "New Task" row).
   */
  const hasActiveFilters = useMemo(() => {
    if (defaultFilterDocument) {
      return !isDefaultFilterDocument(
        filterDocument as FilterDocument,
        defaultFilterDocument as FilterDocument,
      );
    }

    return (
      !!filterDocument.filters &&
      Object.values(filterDocument.filters).some((f) => f !== null)
    );
  }, [filterDocument, defaultFilterDocument]);

  // If we have sections and are in section mode, we consider that a valid result state
  const isSectionGroupingWithSections =
    groupMode === GroupByMode.Section && sections.length;

  if (
    !tasks.length &&
    hasActiveFilters &&
    onReset &&
    !isSectionGroupingWithSections &&
    !normalizedSearchQuery
  ) {
    return <NoFilterResultsState onReset={onReset} dense={denseFormat} />;
  }

  if (!tasks.length && EmptyState && !normalizedSearchQuery) {
    return <EmptyState />;
  }

  return (
    <>
      <GroupedList
        groupSettings={groupSetting}
        tasks={tasks}
        lifeAreas={lifeAreas}
        goals={goals}
        tags={tags}
        sortingMode={normalizeSortingMode(filterDocument.sortingMode)}
        onReorder={onReorder}
        customOrder={customOrder}
        collapsedSections={collapsedSections}
        onSectionCollapse={onSectionCollapse}
        weekStartsOn={weekStartsOn}
        scrollElement={scrollElement}
        denseFormat={denseFormat}
        filterDocument={filterDocument}
        isContextMenuOpen={!!position}
        defaultNewTaskDate={defaultNewTaskDate}
        canReorderGoals={canReorderGoals}
      />

      {!tasks.length && !!normalizedSearchQuery && (
        <NoSearchResultsState dense={denseFormat} />
      )}

      {position &&
        (payload?.tasks?.length === 1 ? (
          <TaskCardContextMenu
            location={position}
            onClose={close}
            task={payload.tasks[0]}
            initialActiveSubMenu={payload.initialActiveSubMenu}
          />
        ) : (
          <TaskCardMultiSelectContextMenu
            location={position}
            onClose={close}
            tasks={payload?.tasks ?? []}
            initialActiveSubMenu={payload?.initialActiveSubMenu}
          />
        ))}
    </>
  );
};

export const TaskFilterList: React.FC<TaskFilterListProps> = (props) => (
  <ContextMenuProvider>
    <TaskFilterListContent {...props} />
  </ContextMenuProvider>
);
